// src/Views/HowItWorks/HowItWorks.js
import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import './PremiumHowItWorks.css'; // Renamed for consistency

const HowItWorks = () => {
    const [activeTab, setActiveTab] = useState('football'); // 'football' | 'crypto'

    return (
        <section className="hiw">
            <div className="hiw__inner">

                <header className="hiw__header">
                    <div className="hiw__eyebrow">
                        <Icon icon="solar:info-circle-linear" className="hiw__eyebrow-icon" />
                        <span>System Manual</span>
                    </div>
                    <h1 className="hiw__title">
                        Understand the engine before<br />
                        <span className="hiw__highlight">you stake a single cedi.</span>
                    </h1>
                    <p className="hiw__subtitle">
                        A quick tour of how Football AI and Crypto AI behave, what Premium unlocks
                        and how the 1-week free trial fits into your journey.
                    </p>
                </header>

                <div className="hiw__controls">
                    <div className="hiw__tabs">
                        <button
                            type="button"
                            className={`hiw__tab ${activeTab === 'football' ? 'hiw__tab--active' : ''}`}
                            onClick={() => setActiveTab('football')}
                        >
                            <Icon icon="solar:soccer-linear" className="hiw__tab-icon" />
                            <span>Football AI</span>
                        </button>
                        <button
                            type="button"
                            className={`hiw__tab ${activeTab === 'crypto' ? 'hiw__tab--active' : ''}`}
                            onClick={() => setActiveTab('crypto')}
                        >
                            <Icon icon="solar:bitcoin-refresh-linear" className="hiw__tab-icon" />
                            <span>Crypto AI</span>
                        </button>
                    </div>
                </div>

                <div className="hiw__content">
                    {activeTab === 'football' ? (
                        <div className="hiw__panel">
                            <div className="hiw__panel-head">
                                <h2 className="hiw__panel-title">Football AI Overview</h2>
                                <p className="hiw__panel-desc">
                                    AiPredict scans fixtures across top leagues and selected lower divisions
                                    to find games where our models see a meaningful edge over the bookmaker.
                                    Every match comes with a market type, AI edge, and confidence score.
                                </p>
                            </div>

                            <div className="hiw__grid">
                                <div className="hiw__card">
                                    <h3 className="hiw__card-title">Freemium vs Premium</h3>
                                    <ul className="hiw__list">
                                        <li>
                                            <Icon icon="solar:check-circle-bold" className="hiw__list-icon" />
                                            <span>
                                                <strong>Freemium tickets</strong> show a small sample of games labelled by
                                                Yesterday, Today and Tomorrow, so you can learn our style without pressure.
                                            </span>
                                        </li>
                                        <li>
                                            <Icon icon="solar:check-circle-bold" className="hiw__list-icon" />
                                            <span>
                                                <strong>Premium Matches</strong> unlock hand-picked singles where the edge is
                                                sharper and the odds are more attractive (3–5 games/day).
                                            </span>
                                        </li>
                                        <li>
                                            <Icon icon="solar:check-circle-bold" className="hiw__list-icon" />
                                            <span>
                                                Each Premium ticket includes confidence %, AI edge, and a
                                                stake guide (% of bankroll).
                                            </span>
                                        </li>
                                    </ul>
                                </div>

                                <div className="hiw__card">
                                    <h3 className="hiw__card-title">HR / HR Lane</h3>
                                    <ul className="hiw__list">
                                        <li>
                                            <Icon icon="solar:flame-bold" className="hiw__list-icon hiw__list-icon--warn" />
                                            <span>
                                                HR/HR tickets are <strong>high-volatility</strong> ideas – long prices, bold calls
                                                or builders – clearly separated from the core list.
                                            </span>
                                        </li>
                                        <li>
                                            <Icon icon="solar:flame-bold" className="hiw__list-icon hiw__list-icon--warn" />
                                            <span>
                                                Designed for <strong>tiny stakes</strong> only (0.25–0.5% bankroll).
                                                Never make this your main strategy.
                                            </span>
                                        </li>
                                        <li>
                                            <Icon icon="solar:flame-bold" className="hiw__list-icon hiw__list-icon--warn" />
                                            <span>
                                                Goal: Catch occasional oversized wins without hurting core capital.
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div className="hiw__footer-note">
                                <h3>Safe Usage Protocol</h3>
                                <ul>
                                    <li>Set a bankroll you can afford to lose.</li>
                                    <li>Stake 1–2% of bankroll on core Premium singles.</li>
                                    <li>Use Freemium slips as training wheels before scaling.</li>
                                    <li>Reserve HR/HR for fun, tiny-stake shots only.</li>
                                </ul>
                            </div>
                        </div>
                    ) : (
                        <div className="hiw__panel">
                            <div className="hiw__panel-head">
                                <h2 className="hiw__panel-title">Crypto AI Overview</h2>
                                <p className="hiw__panel-desc">
                                    Crypto AI is a <strong>Premium-only</strong> feature that generates structured trade
                                    ideas: entry zones, invalidation levels, and take-profit targets. It never touches your
                                    funds and never executes trades for you.
                                </p>
                            </div>

                            <div className="hiw__lock-box">
                                <Icon icon="solar:lock-keyhole-bold" className="hiw__lock-icon" />
                                <div className="hiw__lock-content">
                                    <h4>Requires Premium Access</h4>
                                    <p>
                                        Subscribe to unlock Crypto AI and Premium Football – your subscription starts
                                        with a <strong>free 1-week trial</strong>. Cancel anytime before the trial ends to pay $0.
                                    </p>
                                </div>
                            </div>

                            <div className="hiw__grid">
                                <div className="hiw__card">
                                    <h3 className="hiw__card-title">What Crypto AI Provides</h3>
                                    <ul className="hiw__list">
                                        <li>
                                            <Icon icon="solar:chart-2-bold" className="hiw__list-icon" />
                                            <span>Directional bias (Long, Short, Neutral).</span>
                                        </li>
                                        <li>
                                            <Icon icon="solar:chart-2-bold" className="hiw__list-icon" />
                                            <span>Entry zones, invalidation levels, and realistic targets.</span>
                                        </li>
                                        <li>
                                            <Icon icon="solar:chart-2-bold" className="hiw__list-icon" />
                                            <span>Risk tags: Spot, Conservative, or Degen style.</span>
                                        </li>
                                    </ul>
                                </div>

                                <div className="hiw__card">
                                    <h3 className="hiw__card-title">Trial Integration</h3>
                                    <ul className="hiw__list">
                                        <li>
                                            <Icon icon="solar:shield-check-bold" className="hiw__list-icon" />
                                            <span>
                                                Activate Premium to start your <strong>1-week trial</strong>.
                                                Both Crypto AI and Football AI unlock instantly.
                                            </span>
                                        </li>
                                        <li>
                                            <Icon icon="solar:shield-check-bold" className="hiw__list-icon" />
                                            <span>
                                                If you cancel during trial, access ends when the week finishes.
                                                Freemium remains forever.
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div className="hiw__footer-note">
                                <h3>Responsible Trading</h3>
                                <ul>
                                    <li>Keep a dedicated crypto bankroll separate from sports money.</li>
                                    <li>Risk only a small slice per idea (1–3% for spot).</li>
                                    <li>Assume HR/HR ideas can go to zero.</li>
                                    <li>Use AiPredict to structure thinking, not replace it.</li>
                                </ul>
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </section>
    );
};

export default HowItWorks;